/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * MarketingPanel Component - Marketing content for authentication pages
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, Target, BookOpen } from 'lucide-react'
import { useAuthContent } from '../../data/authContent'
import { useBranding } from '../../stores/brandingStore'
import DeaverisLogo from '../../assets/deaveris.svg'
import MarketingLeaderboard from './MarketingLeaderboard'
import PromotionalEvents from './PromotionalEvents'

/**
 * MarketingPanel Component
 *
 * Displays marketing content including branding and value propositions
 */
const MarketingPanel = () => {
  const authContent = useAuthContent()
  const branding = useBranding()
  return (
    <div className="flex flex-col h-full justify-between">
      {/* Logo and Branding */}
      <div className="mb-12">
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4 p-1">
            <img
              src={branding.logoUrl || branding.iconUrl || DeaverisLogo}
              alt={branding.name}
              className="w-full h-full"
            />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{authContent.branding.logo}</h1>
            <p className="text-primary-100 text-sm">{authContent.branding.tagline}</p>
          </div>
        </div>
        <p className="text-primary-50 text-lg leading-relaxed">
          {authContent.branding.description}
        </p>
      </div>

      {/* Value Propositions */}
      <div className="mb-8 space-y-6">
        {authContent.valuePropositions.map((prop, index) => (
          <motion.div
            key={prop.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
            className="flex items-start space-x-4"
          >
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
              {prop.icon === 'brain' && <Target className="w-5 h-5 text-white" />}
              {prop.icon === 'chart' && <TrendingUp className="w-5 h-5 text-white" />}
              {prop.icon === 'book' && <BookOpen className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1">{prop.title}</h3>
              <p className="text-primary-100 text-sm">{prop.description}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Promotional Events */}
      <PromotionalEvents />

      {/* Leaderboard Carousel */}
      <MarketingLeaderboard />
    </div>
  )
}

export default MarketingPanel
